<template>
  <div>
    <div class="planned-activity-product-row">
      <p>
        <strong class="mr-2">
          {{ `${activityProduct.withtax_price.amount / 100}${activityProduct.withtax_price.currency.symbol}` }}
        </strong>
        {{ activityProduct.name }}
      </p>
      <p class="text-grey">
        {{ activityProduct.description }}
      </p>
      <div class="text-right mt-3">
        <b-button
          :disabled="remainingPlaces <= 0"
          type="is-primary"
          rounded
          @click="openQuantityModal"
        >
          <span v-if="slotable || !activityProduct.is_free">
            {{ $t(slotable ? 'choose' : 'addToCart') }}
          </span>
          <span v-else>
            {{ $t('register') }}
          </span>
        </b-button>
      </div>
    </div>

    <b-modal
      v-model="addedToCardModal"
      :width="activityProduct.is_free ? 500 : 350"
      @close="closeAddModal"
    >
      <div class="card">
        <div class="card-content p-4">
          <p class="title is-5 mb-1">
            {{ activityProduct.name }}
          </p>
          <p class="is-6 mb-0">
            <b-icon icon="clock-outline" size="is-small" />
            {{ start() }}
          </p>
          <p v-if="showRemainingPlace" class="is-6 mb-0">
            {{ $tc('remainingPlaces', remainingPlaces, { count: remainingPlaces }) }}
          </p>
          <div class="content mt-3">
            <!-- ActivityProduct is not free -->
            <div v-if="slotable || !activityProduct.is_free">
              <!-- For add to basket -->
              <div v-if="!addedToCard">
                <div class="columns is-vcentered is-mobile mb-0">
                  <div
                    class="column pr-0"
                    style="max-width: 95px;"
                  >
                    <strong>{{ $t("quantity") }} :</strong>
                  </div>
                  <div class="column">
                    <b-field>
                      <b-select
                        v-model="quantity"
                        expanded
                      >
                        <option
                          v-for="(index, quantityIndex) in reservablePlaces"
                          :key="`quantity-${quantityIndex}`"
                        >
                          {{ index }}
                        </option>
                      </b-select>
                    </b-field>
                  </div>
                </div>
                <b-button
                  expanded
                  rounded
                  class="button button-cart is-primary add-cart-button loadable-button"
                  :title="$t('addToCart')"
                  icon-left="cart-arrow-down"
                  style="font-size: 1em"
                  @click="cartOrSlot()"
                >
                  {{ $t(slotable ? 'choose' : 'addToCart') }}
                </b-button>
              </div>

              <!-- success add to card -->
              <div v-if="addedToCard">
                <div class="message is-success mt-2 mb-2">
                  <div class="message-body">
                    <b-icon icon="cart-plus" class="has-text-success mr-1" />
                    {{ $tc("successToAdded", quantityAdded, { count: quantityAdded }) }} {{ $t("cart") }}
                  </div>
                </div>
                <div class="columns is-mobile">
                  <div class="column">
                    <b-button
                      expanded
                      rounded
                      @click="closeAddModal"
                    >
                      {{ $t("continueShopping") }}
                    </b-button>
                  </div>
                  <div class="column pl-0">
                    <a
                      class="button is-fullwidth is-rounded is-primary"
                      :href="osvConfig.paths.cartProduct"
                    >
                      {{ $t("myCart") }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <!-- ActivityProduct is free -->
            <div v-else>
              <div v-if="!participantSaved">
                <div v-if="loadingCustomerAccount" class="text-center p-5 m-5">{{ $t('pleaseWait') }}</div>
                <div v-else>
                  <p>{{ $t('completeForm') }}</p>
                  <b-field
                      :label="$t('firstname')"
                      custom-class="required"
                      label-position="on-border"
                  >
                    <b-input required v-model="firstname" />
                  </b-field>
                  <b-field
                      :label="$t('lastname')"
                      custom-class="required"
                      label-position="on-border"
                  >
                    <b-input required v-model="lastname" />
                  </b-field>
                  <date-of-birth v-model="dateOfBirth" />
                  <b-field
                      :label="$t('phone')"
                      label-position="on-border"
                  >
                    <b-input
                        required
                        v-model="phone"
                        type="tel"
                        v-cleave="masks.frenchPhone"
                        @input.native="onPhoneNumberInput"
                        @keydown.native="phoneNumberKeyDown"
                        placeholder="06-00-00-00-00"
                        maxlength="14"
                        :has-counter="false"
                    />
                  </b-field>
                  <b-field
                      :label="$t('email')"
                      custom-class="required"
                      label-position="on-border"
                  >
                    <b-input required v-model="email" type="email" />
                  </b-field>
                  <!-- Checkbox list -->
                  <div class="form-group">
                    <b-checkbox v-model="newsletter">
                      {{ $t("newsletter", { name: activityProduct.tenant_name }) }}
                      <cite class="text-grey">{{ $t("newsletterPerMonth") }}</cite>
                    </b-checkbox>
                  </div>
                  <div class="form-group">
                    <b-checkbox v-model="commercialOffer">
                      {{ $t("commercialOffer", { name: activityProduct.tenant_name }) }}
                      <cite class="text-grey">{{ $t("promosAndOffers") }}</cite>
                    </b-checkbox>
                  </div>
                  <div class="form-group">
                    <b-checkbox v-model="acceptGdpr">
                      {{ $t("acceptGdpr") }}
                    </b-checkbox>
                    <a
                        :href="osvConfig.paths.gdprCustomer"
                        v-if="osvConfig.paths.gdprCustomer"
                        target="_blank"
                    >
                      {{ $t("gdpr") }}
                    </a>
                  </div>
                  <div v-if="error">
                    <b-message type="is-warning">
                      <u>{{ $t('errorRegistration') }}</u>.<br>
                      {{ this.errorMessage }}
                    </b-message>
                  </div>
                  <b-button
                      expanded
                      rounded
                      class="button button-cart is-primary add-cart-button loadable-button mt-2"
                      :title="$t('register')"
                      style="font-size: 1em"
                      @click="registerParticipant()"
                  >
                    {{ $t('save') }}
                  </b-button>
                  <b-button
                      v-if="!isLoggedIn"
                      tag="a"
                      expanded
                      rounded
                      class="mt-2"
                      :href="`${osvConfig.paths.signIn}?redirect_to=${thisLocation()}`"
                  >
                    {{ $t('connection') }}
                  </b-button>
                </div>
              </div>
              <div v-if="participantSaved">
                <div class="message is-success mt-2 mb-2">
                  <div class="message-body">
                    <b-icon icon="account-plus" class="has-text-success mr-1" />
                    {{ $t("participantRegistered") }}
                  </div>
                </div>
                <div class="columns is-mobile">
                  <div class="column">
                    <b-button
                        expanded
                        rounded
                        @click="closeAddModal"
                    >
                      {{ $t("continueShopping") }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Failed message -->
    <osv-app-alert
      osv-alert-position="osv-alert-relative"
      :scrolling="false"
      :direct-error="error"
    />
  </div>
</template>

<script>
import { LocaleMixin } from "@/mixins/LocaleMixin";
import { HelpersMixin } from "@/mixins/HelpersMixin";
import { ConfigMixin } from "@/mixins/ConfigMixin";
import { PhoneNumberMixin } from "@/mixins/PhoneNumberMixin";
import VueI18n from "vue-i18n";
import store from "@/store";
import OsvAppAlert from "@/components/shared/OsvAppAlert";
import DateOfBirth from "@/components/ui/DateOfBirth.vue";
import moment from "@/services/momentjs";
import cug from "@/services/cug";

export default {
  store,
  name: "PlannedActivityProductRow",
  components: { DateOfBirth, OsvAppAlert },
  mixins: [LocaleMixin, HelpersMixin, ConfigMixin, PhoneNumberMixin],
  props: {
    plannedActivity: Object,
    activityProduct: Object,
    timeSlotFormat: String,
    slotable: {
      type: Boolean,
      default: false
    },
    showRemainingPlace: {
      type: Boolean,
      default: true
    },
    maxSlotablePlaces: {
      type: [Number, String],
      default: null
    },
    slotableCallback: Function,
    getDailyActivity: {
      type: Function,
      default: null
    },
    directToCard: {
      type: Boolean,
      required: false
    }
  },

  data() {
    return {
      quantity: 1,
      firstname: "",
      lastname: "",
      dateOfBirth: null,
      phone: "",
      email: "",
      newsletter: false,
      commercialOffer: false,
      acceptGdpr: false,
      errorMessage: "",
      addedToCard: false,
      quantityAdded: 0,
      error: null,
      addedToCardModal: false,
      participantSaved: false,
      loadingCustomerAccount: true
    };
  },

  computed: {
    remainingPlaces () {
      if (this.plannedActivity.max === null) {
        return 100000;
      } else {
        return this.plannedActivity.max - this.plannedActivity.participants_count - (this.plannedActivity.reservations_in_baskets || 0);
      }
    },

    reservablePlaces () {
      const maxPlace = this.plannedActivity.max || 10000
      const remainingPlaceDate = maxPlace - this.plannedActivity.participants_count - (this.plannedActivity.reservations_in_baskets || 0);
      return this.slotable && this.maxSlotablePlaces < remainingPlaceDate ? this.maxSlotablePlaces : remainingPlaceDate;
    },

    isLoggedIn () {
      return store.getters["auth/isLoggedIn"];
    }
  },

  methods: {
    start () {
      return moment(this.plannedActivity.start_at).format(this.timeSlotFormat);
    },

    cartOrSlot () {
      if (this.slotable) {
        this.bookSlot()
      } else {
        this.addToCart()
      }
    },

    openQuantityModal () {
      this.loadingCustomerAccount = true
      if (this.slotable && this.reservablePlaces === 1) {
        this.quantity = 1;
        this.bookSlot();
      } else {
        this.addedToCardModal = true;
      }
      if (this.isLoggedIn) {
        cug
          .getAccount()
          .then(resp => {
            const customer = resp.data.customers[0]
            this.firstname = customer.firstname
            this.lastname = customer.lastname
            this.dateOfBirth = customer.date_of_birth
            this.phone = customer.phone
            this.email = customer.email
          })
          .finally(() => {
            this.loadingCustomerAccount = false
          });
      } else {
        this.loadingCustomerAccount = false;
      }
    },

    addToCart () {
      let data = {
        id: this.plannedActivity.id,
        product_id: this.activityProduct.id,
        quantity: this.quantity
      };

      this.loadButton(true);
      this.quantityAdded = this.quantity;

      store
        .dispatch("cart/addPlannedActivity", data)
        .then(() => {
          if (this.addedToCard) {
            location.href = this.osvConfig.paths.cartProduct;
          } else {
            this.addedToCard = true;
            this.getDailyActivity();
          }
        })
        .catch(err => {
          this.error = {
            event: "OsvAlert_ErrorFromApi",
            err: err,
            object: "Booking"
          };
        })
        .finally(() => {
          this.loadButton(false);
        });
    },

    bookSlot () {
      this.slotableCallback({
        type: "Activity",
        plannedActivity: this.plannedActivity,
        activityProduct: this.activityProduct,
        quantity: this.quantity
      });
    },

    closeAddModal () {
      this.addedToCard = false;
      this.addedToCardModal = false;
      this.participantSaved = false;
      this.error = false;
    },

    registerParticipant () {
      this.loadButton(true);
      cug
        .registerActivityParticipant({
          planned_activity_date_id: this.plannedActivity.id,
          activity_product_id: this.activityProduct.id,
          firstname: this.firstname,
          lastname: this.lastname,
          date_of_birth: this.dateOfBirth,
          phone: this.phone,
          email: this.email,
          newsletter: this.newsletter,
          commercial_offer: this.commercialOffer,
          accept_gdpr: this.acceptGdpr
        })
        .then(() => {
          this.participantSaved = true;
          this.getDailyActivity();
          this.resetForm();
        })
        .catch((err) => {
          this.error = true;
          this.errorMessage = err.response.data.base[0];
        })
        .finally(() => {
          this.loadButton(false);
        });
    },

    resetForm () {
      this.firstname = ""
      this.lastname = ""
      this.dateOfBirth = ""
      this.phone = ""
      this.email = ""
      this.newsletter = false
      this.commercialOffer = false
      this.acceptGdpr = false
    },

    thisLocation () {
      return document.location.pathname
    }
  },

  // Translate component
  mounted () {
    this.$i18n.locale = this.getLocale();
  },
  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        remainingPlaces: "%{count} remaining place | {count} remaining places",
        addToCart: "Add to cart",
        places: "Places",
        error: "An error occurred while adding to the cart.",
        errorRegistration: "An error occurred while adding the participant.",
        successToAdded: "%{count} product added to | {count} products added to",
        cart: "cart",
        choose: "Choose",
        myCart: "My cart",
        continueShopping: "Continue shopping",
        start: "Start",
        quantity: "Quantity",
        register: "Register",
        isFree: "Free",
        firstname: "Firstname",
        lastname: "Lastname",
        phone: "Phone",
        email: "Email",
        save: "Save",
        connection: "Sign in",
        participantRegistered: "Successful registration",
        completeForm: "To register, please complete the following form:",
        newsletter: "I would like to receive the newsletter of {name}",
        newsletterPerMonth: "(1 to 2 per month)",
        commercialOffer: "I would like to receive good maps of {name}",
        promosAndOffers: "(Promotions, personalized offers, etc.)",
        acceptGdpr: "I have read and agree to the processing of my personal data",
        gdpr: "Data Management Policy",
        youMustAcceptGdpr: "You must accept the {name}'s data management policy",
        pleaseWait: "Please wait"
      },
      fr: {
        remainingPlaces: "%{count} place restante | {count} places restantes",
        addToCart: "Ajouter au panier",
        places: "Places",
        error: "Une erreur s'est produite lors de l'ajout au panier.",
        errorRegistration: "Une erreur s'est produite lors de l'ajout du participant.",
        successToAdded:
          "%{count} produit ajouté au | {count} produits ajoutés au",
        cart: "panier",
        choose: "Choisir",
        myCart: "Mon panier",
        continueShopping: "Continuer mes achats",
        start: "Début",
        quantity: "Quantité",
        register: "S'inscrire",
        isFree: "Gratuit",
        firstname: "Prénom",
        lastname: "Nom de famille",
        phone: "Téléphone",
        email: "Email",
        save: "Enregistrer",
        connection: "Me connecter",
        participantRegistered: "Inscription réussie",
        completeForm: "Pour vous inscrire, veuillez compléter le formulaire suivant :",
        newsletter: "Je souhaite recevoir la newsletter de {name}",
        newsletterPerMonth: "(1 à 2 par mois)",
        commercialOffer: "Je souhaite recevoir les bons plans de {name}",
        promosAndOffers: "(Promos, offres personnalisées, etc.)",
        acceptGdpr: "J'ai lu et j'accepte le traitement de mes données personnelles",
        gdpr: "Politique de gestion des données",
        youMustAcceptGdpr: "Vous devez accepter la politique de gestion des données de {name}",
        pleaseWait: "Veuillez patienter"
      }
    }
  })
};
</script>

<style lang="scss" src="../../assets/styles/buefy/base.scss"></style>
<style lang="scss">
.planned-activity-product-row {
  .quantity-field {
    display: inline-block;
    width: 200px;

    .control.plus {
      .button {
        border-top: 1px solid #dbdbdb;
        border-bottom: 1px solid #dbdbdb;
      }
    }

    .control input[type="number"] {
      border-right: none;
      border-left: none;
      text-align: center;

      &:hover {
        border-color: #dbdbdb;
      }
    }

    .control.minus {
      .button {
        border-top: 1px solid #dbdbdb;
        border-bottom: 1px solid #dbdbdb;
        border-left: 1px solid #dbdbdb;
      }
    }

    label {
      margin-left: -7px;
    }

    .button {
      height: 100%;
    }

    .button-cart {
      width: 40px;
      padding-top: 10px;
    }
  }
  .use-ticket-btn {
    button {
      text-decoration: none;
      text-transform: lowercase;
      span.use-ticket-or {
        margin-right: 4px;
        font-weight: normal;
      }
      span.use-ticket-text {
        color: #007bff;
        text-decoration: underline;
      }
    }
  }
}
</style>
